import React, { useMemo, useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from "react-i18next";
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { formatCurrency, getCurrencySymbol, formatCompactNumber } from '../charts/chartTheme';
import {
    TrendingUp,
    Sparkles,
    Zap,
    Trophy,
    ChevronDown,
    ChevronUp,
} from 'lucide-react';

import {
    format,
    parseISO,
    eachDayOfInterval,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    getWeek,
    startOfDay
} from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { InfoModal } from '../modals/InfoModal';
import { calculateRevenueSourceBreakdown } from './forecast/revenueSourceBreakdown';
import {
    // UI Components
    RevenueSourceBreakdownUI,
    SourceIcon
} from './forecast';
import { RevenueSubSourceBreakdown } from './forecast/revenueSubSourceBreakdown';
import { getSourceRowColor } from './forecast/revenueSubSourceBreakdownColors';
import { BoardBottomDrawer } from '../BoardBottomDrawer';
import { AIVerdictModule } from '../AIVerdictModule';
import { useBoardStore } from '../../../../stores/boardStore';

// ============================================================
// SHARED COMPONENTS (Defined outside to prevent unmounting)
// ============================================================

const KPICard = React.memo(({ title, value, unit = '', icon: Icon, color, isCurrency = true, precision = 2, formatCurrency, currencySymbol }: any) => (
    <div className="bg-white dark:bg-neutral-900 rounded-xl p-4 border border-neutral-200 dark:border-neutral-800 shadow-sm hover:shadow-md transition-all duration-300 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center gap-2 mb-2">
            <div className={`p-1.5 rounded-lg ${color} bg-opacity-10 dark:bg-opacity-20`}>
                <Icon className={`w-3.5 h-3.5 ${color.replace('bg-', 'text-')}`} />
            </div>
            <h3 className="text-[11px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider truncate mr-4">{title}</h3>
        </div>
        <div className="text-xl font-black text-neutral-900 dark:text-neutral-100 truncate">
            {isCurrency ? formatCurrency(value, currencySymbol) :
                (typeof value === 'string' ? value : `${(value || 0).toFixed(precision)}${unit}`)}
        </div>
    </div>
));

const DistributionCard = React.memo(({
    data,
    title,
    icon: Icon,
    color = "text-indigo-500",
    bgColor = "bg-indigo-500",
    externalHoverSource = null,
    onHoverChange = null
}: any) => {
    const [localHover, setLocalHover] = useState<string | null>(null);

    const sortedSources = (data.barSources || data.sources)
        .slice()
        .sort((a: any, b: any) => b.attribution - a.attribution);

    const handleMouseEnter = (name: string) => {
        setLocalHover(name);
        onHoverChange?.(name);
    };

    const handleMouseLeave = () => {
        setLocalHover(null);
        onHoverChange?.(null);
    };

    return (
        <div className="flex-1 bg-white dark:bg-neutral-900 rounded-xl p-4 border border-neutral-200 dark:border-neutral-800 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="flex items-center gap-2 mb-4">
                <div className={`p-1.5 rounded-lg ${bgColor} bg-opacity-10 dark:bg-opacity-20`}>
                    <Icon className={`w-3.5 h-3.5 ${color}`} />
                </div>
                <h3 className="text-[11px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider truncate mr-4">
                    {title}
                </h3>
            </div>

            <div className="relative">
                <div className="h-8 w-full bg-neutral-100 dark:bg-neutral-800 rounded-lg overflow-hidden flex">
                    {sortedSources.map((source: any) => {
                        const tableIndex = (data.barSources || data.sources).findIndex((s: any) => s.name === source.name);
                        const segmentColor = getSourceRowColor(tableIndex);
                        const isHighlighted = (localHover === source.name) || (externalHoverSource === source.name);

                        return (
                            <div
                                key={source.name}
                                style={{
                                    width: `${source.attribution}%`,
                                    backgroundColor: segmentColor
                                }}
                                className={`h-full group relative cursor-pointer transition-all duration-300 border-r border-white/10 last:border-0 flex items-center justify-center ${isHighlighted ? 'brightness-110 z-[10]' : ''}`}
                                onMouseEnter={() => handleMouseEnter(source.name)}
                                onMouseLeave={handleMouseLeave}
                            >
                                {source.attribution > 5 && (
                                    <div className="flex items-center gap-0.5 opacity-90 group-hover:opacity-100 transition-opacity pointer-events-none px-0.5 overflow-hidden">
                                        <SourceIcon name={source.name} className="w-3 h-3 shrink-0 text-white" />
                                        {source.attribution > 15 && (
                                            <span className="text-[8px] font-black text-white uppercase tracking-tighter truncate leading-none">
                                                {source.name}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {sortedSources.map((source: any, idx: number, sortedArr: any[]) => {
                    const isHovered = localHover === source.name;
                    let prevCumulative = 0;
                    for (let i = 0; i < idx; i++) prevCumulative += sortedArr[i].attribution;
                    const center = prevCumulative + source.attribution / 2;

                    let shift = -50;
                    if (center < 10) shift = -50 + (10 - center) * 5;
                    else if (center > 90) shift = -50 - (center - 90) * 5;
                    const finalShift = Math.max(-100, Math.min(0, shift));

                    return (
                        <div
                            key={`tooltip-${source.name}`}
                            style={{ left: `${center}%` }}
                            className={`absolute top-0 bottom-0 pointer-events-none ${isHovered ? 'z-[100]' : 'z-[40]'}`}
                        >
                            <div
                                style={{ transform: `translateX(${finalShift}%)` }}
                                className={`absolute transition-all duration-300 ${isHovered ? 'opacity-100 -top-10' : 'opacity-0 -top-8'} whitespace-nowrap`}
                            >
                                <div className="bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 text-[9px] font-black uppercase tracking-[0.2em] px-4 py-2 rounded shadow-2xl border border-white/20 dark:border-black/10 flex items-center gap-2">
                                    <SourceIcon name={source.name} className="w-3 h-3 text-white dark:text-neutral-900" />
                                    <span>
                                        {source.name}: {source.attribution.toFixed(1)}%
                                        {source.paidAttribution > 0 && ` | Paid: ${source.paidAttribution.toFixed(1)}%`}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

const ConversionAttributionTable = React.memo(({
    metricMappings,
    selectedMetricName,
    setSelectedMetricName,
    activeMetric,
    isMetricDrawerOpen,
    setIsMetricDrawerOpen,
    pendingMetricName,
    setPendingMetricName,
    mappingCardRef,
    setSelectedSourceLink,
    setInfoModal,
    formatCurrency,
    currencySymbol,
    t
}: any) => {
    useEffect(() => {
        if (!isMetricDrawerOpen && pendingMetricName) {
            const timer = setTimeout(() => {
                setSelectedMetricName(pendingMetricName);
                setPendingMetricName(null);
            }, 400);
            return () => clearTimeout(timer);
        }
    }, [isMetricDrawerOpen, pendingMetricName, setSelectedMetricName, setPendingMetricName]);

    return (
        <div
            ref={mappingCardRef}
            className={`bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm mt-8 flex flex-col relative transition-all duration-500 ${isMetricDrawerOpen ? 'mb-96' : 'mb-8'}`}
        >
            <div className="px-6 py-4 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between z-20 bg-white dark:bg-neutral-900 rounded-t-2xl">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setIsMetricDrawerOpen(!isMetricDrawerOpen)}
                        className="p-1.5 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-400 hover:text-accent-primary hover:bg-accent-primary/5 transition-all"
                    >
                        {isMetricDrawerOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    {activeMetric && (
                        <div className="flex flex-col">
                            <span className="text-[9px] font-black text-neutral-400 uppercase tracking-widest leading-none mb-1">{t('pages.traffic.activeFocus')}</span>
                            <span className="text-xs font-bold text-accent-primary leading-none">{activeMetric.name}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="relative flex-1">
                <AnimatePresence>
                    {isMetricDrawerOpen && (
                        <motion.div
                            initial={{ height: 0, opacity: 0, scaleY: 0.95 }}
                            animate={{ height: "auto", opacity: 1, scaleY: 1 }}
                            exit={{ height: 0, opacity: 0, scaleY: 0.95 }}
                            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                            className="absolute inset-x-0 top-0 z-30 origin-top overflow-hidden bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] dark:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)]"
                        >
                            <div className="bg-neutral-50/50 dark:bg-neutral-800/20">
                                <div className="overflow-x-auto border-b border-neutral-200/50 dark:border-neutral-700/50 bg-white dark:bg-neutral-900 shadow-sm max-h-[350px] fade-scrollbar">
                                    <table className="w-full text-left border-collapse">
                                        <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                                            {metricMappings.map((m: any) => (
                                                <tr
                                                    key={m.name}
                                                    onClick={() => {
                                                        setPendingMetricName(m.name);
                                                        setIsMetricDrawerOpen(false);
                                                    }}
                                                    className={`cursor-pointer transition-all ${selectedMetricName === m.name ? 'bg-accent-primary/5 dark:bg-accent-primary/10' : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/30'}`}
                                                >
                                                    <td className="px-5 py-3">
                                                        <div className={`text-xs font-bold ${selectedMetricName === m.name ? 'text-accent-primary font-black' : 'text-neutral-700 dark:text-neutral-300'}`}>
                                                            {m.name}
                                                        </div>
                                                    </td>
                                                    <td className="px-5 py-3 text-right">
                                                        <div className="text-xs font-black text-neutral-900 dark:text-neutral-100">
                                                            {formatCurrency(m.totalRevenue, currencySymbol)}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <motion.div
                    animate={{
                        filter: (isMetricDrawerOpen || pendingMetricName) ? 'blur(4px)' : 'blur(0px)',
                        opacity: (isMetricDrawerOpen || pendingMetricName) ? 0.7 : 1
                    }}
                    transition={{ duration: 0.3 }}
                    className="min-h-[300px]"
                >
                    <AnimatePresence mode="popLayout" initial={false}>
                        <motion.div
                            key={selectedMetricName}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                        >
                            <div className="overflow-x-visible">
                                <table className="w-full text-left border-collapse table-auto md:table-fixed">
                                    <thead className="bg-white dark:bg-neutral-900 border-b border-neutral-100 dark:border-neutral-800">
                                        <tr>
                                            <th className="w-[40%] md:w-[30%] px-2 md:px-6 py-3 text-[10px] font-bold text-neutral-400 uppercase tracking-widest">{t('pages.traffic.table.performanceSource')}</th>
                                            <th className="w-[25%] md:w-[15%] px-2 md:px-6 py-3 text-[10px] font-bold text-neutral-400 uppercase tracking-widest text-right">{t('pages.traffic.table.revenueValue')}</th>
                                            <th className="w-[15%] px-2 hidden sm:table-cell py-3 text-[10px] font-bold text-neutral-400 uppercase tracking-widest text-right">{t('pages.traffic.table.sharePercent')}</th>
                                            <th className="w-[35%] md:w-[40%] px-2 md:px-6 py-3 text-[10px] font-bold text-neutral-400 uppercase tracking-widest text-center">{t('pages.traffic.table.connectivityStatus')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                                        {activeMetric?.links.map((link: any, idx: number) => {
                                            const entityTotalRev = activeMetric.totalRevenue || 1;
                                            const sourcePercentage = (link.revenue / entityTotalRev) * 100;

                                            return (
                                                <tr 
                                                    key={idx} 
                                                    onClick={() => setSelectedSourceLink(link)}
                                                    className="hover:bg-neutral-50 dark:hover:bg-neutral-800/30 transition-colors group cursor-pointer"
                                                >
                                                    <td className="px-2 md:px-6 py-4">
                                                        <div className="flex items-center gap-1 flex-wrap">
                                                            <div className="flex items-center gap-1 shrink-0">
                                                                <SourceIcon name={link.source} className="w-3 h-3" />
                                                                <span className="text-xs font-medium text-neutral-600 dark:text-neutral-400 truncate max-w-[60px] sm:max-w-[80px] md:max-w-[120px]">{link.source}</span>
                                                            </div>
                                                            <span className={`px-1 rounded text-[7px] font-black uppercase tracking-tighter shrink-0 ${link.isPaid ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'}`}>
                                                                {link.isPaid ? t('pages.traffic.table.paid') : t('pages.traffic.table.organic')}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-2 md:px-6 py-4 text-right">
                                                        <div className="text-xs font-black text-neutral-900 dark:text-neutral-100">
                                                            {formatCurrency(link.revenue, currencySymbol)}
                                                        </div>
                                                    </td>
                                                    <td className="px-2 hidden sm:table-cell py-4 text-right border-l border-neutral-50 dark:border-neutral-800/10">
                                                        <div className="text-xs font-bold text-accent-primary">
                                                            {sourcePercentage.toFixed(1)}%
                                                        </div>
                                                    </td>
                                                    <td className="px-2 md:px-6 py-4">
                                                        <RevenueSubSourceBreakdown
                                                            link={link}
                                                            onOpenInfoModal={(title: string, description: string) => setInfoModal({ isOpen: true, title, description })}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {(!activeMetric || activeMetric.links.length === 0) && (
                                            <tr>
                                                <td colSpan={4} className="px-6 py-12 text-center text-neutral-400 text-xs font-medium uppercase tracking-widest italic">
                                                    {t('pages.traffic.table.noDataLinkAnalysis')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </motion.div>
            </div>
        </div>
    );
});

interface TrafficPageProps { }

/**
 * TrafficPage - With sticky Total column and Distribution Bar
 */
export const TrafficPage: React.FC<TrafficPageProps> = () => {
    const { t } = useTranslation("common");
    const { finance, currencySettings, dateRange, entities, channels } = useBoardChartData();
    const boardId = useBoardStore(state => state.boardId);
    const currencySymbol = getCurrencySymbol(currencySettings?.default);

    const [viewMode, setViewMode] = useState<'weeks' | 'months' | 'all'>('months');
    const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);
    const [hoveredSource, setHoveredSource] = useState<string | null>(null);

    const { i18n: i18nInstance } = useTranslation();
    const currentLocale = i18nInstance.language === 'fr' ? fr : enUS;

    const [selectedMetricName, setSelectedMetricName] = useState<string | null>(null);
    const [isMetricDrawerOpen, setIsMetricDrawerOpen] = useState(false);
    const [isAiMobileExpanded, setIsAiMobileExpanded] = useState(false);
    const mappingCardRef = useRef<HTMLDivElement>(null);

    const [selectedSourceLink, setSelectedSourceLink] = useState<any | null>(null);

    const [pendingMetricName, setPendingMetricName] = useState<string | null>(null);
    const [persistentSourceLink, setPersistentSourceLink] = useState<any | null>(null);

    useEffect(() => {
        if (selectedSourceLink) {
            setPersistentSourceLink(selectedSourceLink);
        } else {
            // Keep the link data present during the 500ms drawer closing transition
            const timer = setTimeout(() => {
                setPersistentSourceLink(null);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [selectedSourceLink]);

    const [infoModal, setInfoModal] = useState<{ isOpen: boolean; title: string; description: string }>({
        isOpen: false,
        title: '',
        description: ''
    });
    const prevDrawerState = useRef(isMetricDrawerOpen);
    const prevMetricName = useRef(selectedMetricName);

    useEffect(() => {
        const drawerOpening = !prevDrawerState.current && isMetricDrawerOpen;
        const metricChanging = prevMetricName.current !== selectedMetricName && prevMetricName.current !== null;

        if ((drawerOpening || metricChanging) && mappingCardRef.current) {
            mappingCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        prevDrawerState.current = isMetricDrawerOpen;
        prevMetricName.current = selectedMetricName;
    }, [isMetricDrawerOpen, selectedMetricName]);


    // 1. Calculate Periods
    const { periods } = useMemo(() => {
        const start = dateRange.startDate ? startOfDay(dateRange.startDate) : startOfMonth(new Date());
        const end = dateRange.endDate ? startOfDay(dateRange.endDate) : endOfMonth(new Date());
        const intervalDays = eachDayOfInterval({ start, end });

        const generatedPeriods: { title: string, days: Date[] }[] = [];
        if (viewMode === 'weeks') {
            const weeksSet = new Set<string>();
            intervalDays.forEach(day => {
                const wStart = startOfWeek(day, { weekStartsOn: 1 });
                const weekStr = format(wStart, 'yyyy-MM-dd');
                if (!weeksSet.has(weekStr)) {
                    weeksSet.add(weekStr);
                    const weekNum = getWeek(day, { weekStartsOn: 1 });
                    const periodDays = eachDayOfInterval({
                        start: wStart,
                        end: endOfWeek(day, { weekStartsOn: 1 })
                    }).filter(d => d >= start && d <= end);
                    generatedPeriods.push({
                        title: t('charts.components.week_number', { number: weekNum, defaultValue: `Week ${weekNum}` }),
                        days: periodDays
                    });
                }
            });
        } else if (viewMode === 'months') {
            const monthsSet = new Set<string>();
            intervalDays.forEach(day => {
                const mStart = startOfMonth(day);
                const monthStr = format(mStart, 'yyyy-MM');
                if (!monthsSet.has(monthStr)) {
                    monthsSet.add(monthStr);
                    const periodDays = eachDayOfInterval({
                        start: mStart,
                        end: endOfMonth(day)
                    }).filter(d => d >= start && d <= end);
                    generatedPeriods.push({
                        title: format(day, 'MMMM yyyy', { locale: currentLocale }),
                        days: periodDays
                    });
                }
            });
        }
        return { periods: generatedPeriods };
    }, [dateRange, viewMode, t, currentLocale]);

    const activePeriod = useMemo(() => {
        if (viewMode === 'all') {
            const start = dateRange.startDate ? startOfDay(dateRange.startDate) : startOfMonth(new Date());
            const end = dateRange.endDate ? startOfDay(dateRange.endDate) : endOfMonth(new Date());
            return { title: t('charts.components.all', 'All'), days: eachDayOfInterval({ start, end }) };
        }
        return periods[selectedPeriodIndex] || { title: '', days: [] };
    }, [viewMode, periods, selectedPeriodIndex, dateRange]);

    // 2. Attribution Logic Helper
    const calculateAttribution = (days: Date[]) => {
        const activities = finance?.transactions?.filter((activity: any) => activity.isActive !== false) || [];
        const daySet = new Set(days.map(d => format(d, 'yyyy-MM-dd')));

        const sourcesMap: Record<string, {
            paid: Record<string, number>,
            organic: Record<string, number>,
            total: number,
            paidTotal: number,
            organicTotal: number
        }> = {};
        const dailyTotals: Record<string, number> = {};
        let totalRevenue = 0;
        let organicRevenue = 0;
        let paidRevenue = 0;

        activities.forEach(activity => {
            const dateStr = format(parseISO(activity.date), 'yyyy-MM-dd');
            if (!daySet.has(dateStr)) return;

            const traffic = activity.traffic ? (typeof activity.traffic === 'string' ? JSON.parse(activity.traffic) : activity.traffic) : {};
            const target = traffic.target || t('pages.traffic.modals.platforms.directOrganic');
            const isPaid = traffic.isPaid === true;
            const amount = Number(activity.amount) || 0;

            if (activity.type === 'REVENUE') {
                totalRevenue += amount;
                if (isPaid) paidRevenue += amount;
                else organicRevenue += amount;

                if (!sourcesMap[target]) {
                    sourcesMap[target] = { paid: {}, organic: {}, total: 0, paidTotal: 0, organicTotal: 0 };
                }

                const category = isPaid ? 'paid' : 'organic';
                const catTotalKey = isPaid ? 'paidTotal' : 'organicTotal';

                sourcesMap[target][category][dateStr] = (sourcesMap[target][category][dateStr] || 0) + amount;
                sourcesMap[target][catTotalKey] += amount;
                sourcesMap[target].total += amount;
                dailyTotals[dateStr] = (dailyTotals[dateStr] || 0) + amount;
            }
        });

        const PRIORITY_SOURCES = [
            'Meta', 'Facebook', 'Instagram', 'Google', 'TikTok', 'Pinterest', 'Snapchat', 'X', 'LinkedIn'
        ];

        // 1. Generate Bar Segments (Aggregated by Platform)
        const barSources = Object.keys(sourcesMap).map(name => {
            const data = sourcesMap[name];
            return {
                name,
                revenue: data.total,
                paidRevenue: data.paidTotal,
                organicRevenue: data.organicTotal,
                attribution: totalRevenue > 0 ? (data.total / totalRevenue) * 100 : 0,
                paidAttribution: totalRevenue > 0 ? (data.paidTotal / totalRevenue) * 100 : 0,
                organicAttribution: totalRevenue > 0 ? (data.organicTotal / totalRevenue) * 100 : 0
            };
        }).sort((a, b) => {
            const getPriority = (name: string) => {
                const index = PRIORITY_SOURCES.findIndex(p => name.toLowerCase().includes(p.toLowerCase()));
                return index === -1 ? PRIORITY_SOURCES.length : index;
            };
            const priorityA = getPriority(a.name);
            const priorityB = getPriority(b.name);
            if (priorityA !== priorityB) return priorityA - priorityB;
            return b.revenue - a.revenue;
        });

        // 2. Generate Table Rows (Split by Paid/Organic)
        const tableSources: any[] = [];
        barSources.forEach(barSource => {
            const data = sourcesMap[barSource.name];

            if (data.paidTotal > 0) {
                tableSources.push({
                    name: `${barSource.name} - Paid`,
                    parentName: barSource.name,
                    isPaid: true,
                    revenue: data.paidTotal,
                    attribution: totalRevenue > 0 ? (data.paidTotal / totalRevenue) * 100 : 0,
                    dailyData: data.paid
                });
            }

            if (data.organicTotal > 0 || data.total === 0 || barSource.name === 'Direct / Organic') {
                tableSources.push({
                    name: barSource.name,
                    parentName: barSource.name,
                    isPaid: false,
                    revenue: data.organicTotal,
                    attribution: totalRevenue > 0 ? (data.organicTotal / totalRevenue) * 100 : 0,
                    dailyData: data.organic
                });
            }
        });

        return {
            sources: tableSources,
            tableSources,
            barSources,
            dailyTotals,
            summary: {
                totalRevenue,
                organicRevenue,
                paidRevenue,
                topChannel: barSources[0]?.name || '-'
            }
        };
    };

    const periodAttributionData = useMemo(() => calculateAttribution(activePeriod.days), [activePeriod, finance, t]);

    const fullAttributionData = useMemo(() => {
        const start = dateRange.startDate ? startOfDay(dateRange.startDate) : startOfMonth(new Date());
        const end = dateRange.endDate ? startOfDay(dateRange.endDate) : endOfMonth(new Date());
        const intervalDays = eachDayOfInterval({ start, end });
        return calculateAttribution(intervalDays);
    }, [dateRange, finance, t]);

    const attributionData = periodAttributionData;

    const metricMappings = useMemo(() => {
        return calculateRevenueSourceBreakdown(finance, entities, channels, dateRange, t);
    }, [finance, entities, channels, dateRange, t]);

    useEffect(() => {
        if (metricMappings.length > 0 && !selectedMetricName) {
            setSelectedMetricName(metricMappings[0].name);
        }
    }, [metricMappings, selectedMetricName]);

    const activeMetric = metricMappings.find(m => m.name === selectedMetricName) || metricMappings[0];

    // Components are now defined outside as React.memo to prevent unnecessary re-renders and blinking.

    return (
        <div className="h-full flex flex-col bg-white dark:bg-neutral-900 overflow-hidden relative">
            <div className="flex-1 overflow-hidden relative flex flex-col">
                <div 
                    className="flex-1 overflow-y-auto fade-scrollbar px-2 lg:px-4"
                    onScroll={(e) => {
                        const container = e.currentTarget;
                        container.classList.add('scrolling');
                        const existingTimeout = (container as any).dataset.scrollTimeout;
                        if (existingTimeout) clearTimeout(parseInt(existingTimeout));
                        const timeoutId = setTimeout(() => {
                            container.classList.remove('scrolling');
                        }, 1000);
                        (container as any).dataset.scrollTimeout = timeoutId.toString();
                    }}
                >
                    <div className="pt-2 lg:pt-4 space-y-4 lg:space-y-8 pb-32">
                        <div className="flex flex-col xl:flex-row gap-6">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 shrink-0">
                                <KPICard
                                    title={t('pages.traffic.kpis.totalRevenue')}
                                    value={fullAttributionData.summary.totalRevenue}
                                    icon={TrendingUp}
                                    color="bg-emerald-500"
                                    formatCurrency={formatCurrency}
                                    currencySymbol={currencySymbol}
                                />
                                <KPICard
                                    title={t('pages.traffic.kpis.organicRevenue')}
                                    value={fullAttributionData.summary.organicRevenue}
                                    icon={Sparkles}
                                    color="bg-blue-500"
                                    formatCurrency={formatCurrency}
                                    currencySymbol={currencySymbol}
                                />
                                <KPICard
                                    title={t('pages.traffic.kpis.paidRevenue')}
                                    value={fullAttributionData.summary.paidRevenue}
                                    icon={Zap}
                                    color="bg-amber-500"
                                    formatCurrency={formatCurrency}
                                    currencySymbol={currencySymbol}
                                />
                                <KPICard
                                    title={t('pages.traffic.kpis.topChannel')}
                                    value={fullAttributionData.summary.topChannel}
                                    icon={Trophy}
                                    color="bg-teal-500"
                                    isCurrency={false}
                                    formatCurrency={formatCurrency}
                                    currencySymbol={currencySymbol}
                                />
                            </div>

                            <DistributionCard
                                data={fullAttributionData}
                                title={t('pages.traffic.distribution', { defaultValue: 'Revenue Distribution' })}
                                icon={TrendingUp}
                                color="text-indigo-500"
                                bgColor="bg-indigo-500"
                            />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                            <div className="order-2 lg:order-1 lg:col-span-8 flex flex-col bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm overflow-hidden min-h-[500px]">
                                <div className="flex-1 overflow-auto relative">
                                    <table className="w-full text-left border-collapse min-w-max">
                                        <thead className="sticky top-0 z-20 bg-white dark:bg-neutral-900 shadow-[0_1px_0_0_#f0f0f0] dark:shadow-[0_1px_0_0_#262626]">
                                            <tr>
                                                <th className="px-5 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 sticky left-0 bg-white dark:bg-neutral-900 shadow-[1px_0_0_0_#f0f0f0] dark:shadow-[1px_0_0_0_#262626]">{t('pages.traffic.table.source')} {t('pages.traffic.table.metrics')}</th>
                                                {activePeriod.days.map(d => {
                                                    const dateStr = format(d, 'yyyy-MM-dd');
                                                    const dayTotal = attributionData.dailyTotals[dateStr] || 0;
                                                    return (
                                                        <th key={dateStr} className="px-5 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 text-right min-w-[100px] border-l border-neutral-50 dark:border-neutral-800/50">
                                                            {format(d, 'MMM dd', { locale: currentLocale })}
                                                            <div className="text-[10px] text-neutral-400 dark:text-neutral-500 font-normal mt-0.5 flex flex-col items-end">
                                                                <span>{format(d, 'EEE', { locale: currentLocale })}</span>
                                                                {dayTotal > 0 && <span className="font-bold text-accent-primary/80">{formatCompactNumber(dayTotal, currencySymbol)}</span>}
                                                            </div>
                                                        </th>
                                                    );
                                                })}
                                                <th className="px-4 md:px-6 py-3 text-[10px] md:text-xs font-black uppercase tracking-normal md:tracking-widest text-accent-primary text-right bg-white dark:bg-neutral-900 sticky right-0 z-30 shadow-[-1px_0_0_0_#f0f0f0] dark:shadow-[-1px_0_0_0_#262626]">
                                                    <span className="md:hidden">{t('pages.traffic.table.total')}</span>
                                                    <span className="hidden md:inline">{t('pages.traffic.table.periodTotal')}</span>
                                                    {attributionData.summary.totalRevenue > 0 && (
                                                        <div className="text-[10px] text-neutral-400 dark:text-neutral-500 font-bold mt-0.5">
                                                            {formatCompactNumber(attributionData.summary.totalRevenue, currencySymbol)}
                                                        </div>
                                                    )}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-neutral-900 divide-y divide-neutral-100 dark:divide-neutral-800">
                                            {attributionData.sources.map((source, sIdx) => {
                                                const tableIndex = (attributionData.barSources || []).findIndex((s: any) => s.name === (source.parentName || source.name));
                                                const color = getSourceRowColor(tableIndex === -1 ? sIdx : tableIndex);
                                                const isHovered = hoveredSource === source.parentName || hoveredSource === source.name;

                                                    return (
                                                        <tr
                                                            key={source.name}
                                                            onClick={() => {
                                                                const link = activeMetric?.links.find(l => 
                                                                    (l.isPaid ? `${l.source} - Paid` : l.source) === source.name
                                                                );
                                                                if (link) setSelectedSourceLink(link);
                                                            }}
                                                            className={`hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors group cursor-pointer ${isHovered ? 'bg-accent-primary/5 dark:bg-accent-primary/10' : ''}`}
                                                        >
                                                            <td className="px-5 py-2 text-xs font-medium text-neutral-700 dark:text-neutral-300 sticky left-0 bg-white dark:bg-neutral-900 shadow-[1px_0_0_0_#f0f0f0] dark:shadow-[1px_0_0_0_#262626] z-10 transition-colors group-hover:bg-neutral-50 dark:group-hover:bg-neutral-800/50">
                                                                <div className="flex items-center gap-1.5">
                                                                    <SourceIcon name={source.name} className="w-3 h-3 shrink-0" color={color} />
                                                                    <div>
                                                                        <div className="font-bold">{source.name}</div>
                                                                        <div className="text-[9px] text-neutral-400 uppercase tracking-tight">{source.attribution.toFixed(1)}% {t('pages.traffic.table.attributionLabel')}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            {activePeriod.days.map(d => {
                                                                const dateStr = format(d, 'yyyy-MM-dd');
                                                                const rev = source.dailyData[dateStr] || 0;
                                                                const dayTotal = attributionData.dailyTotals[dateStr] || 1;
                                                                const activePct = (rev / dayTotal) * 100;
        
                                                                return (
                                                                    <td key={dateStr} className="px-5 py-2 text-xs text-right whitespace-nowrap text-neutral-600 dark:text-neutral-400 border-l border-neutral-50 dark:border-neutral-800/50 transition-colors group-hover:bg-neutral-50 dark:group-hover:bg-neutral-800/50">
                                                                        {rev === 0 ? <span className="text-neutral-300 dark:text-neutral-700">-</span> : (
                                                                            <div>
                                                                                <div className="font-semibold">{formatCurrency(rev, currencySymbol)}</div>
                                                                                <div className="text-[9px] text-neutral-400 font-medium">{activePct.toFixed(1)}%</div>
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                );
                                                            })}
                                                            <td className="px-4 md:px-6 py-2 text-xs text-right bg-white dark:bg-neutral-900 sticky right-0 z-10 shadow-[-1px_0_0_0_#f0f0f0] dark:shadow-[-1px_0_0_0_#262626] transition-colors group-hover:bg-neutral-50 dark:group-hover:bg-neutral-800/50">
                                                                <div className="font-black text-accent-primary">
                                                                    {formatCurrency(source.revenue, currencySymbol)}
                                                                </div>
                                                                <div className="text-[9px] font-bold text-accent-primary/60">
                                                                    {source.attribution.toFixed(1)}%
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="order-1 lg:order-2 lg:col-span-4 hidden lg:flex flex-col space-y-6">
                                <AIVerdictModule 
                                    pageId="traffic" 
                                    boardId={boardId || ''} 
                                />

                                <DistributionCard
                                    data={periodAttributionData}
                                    title={t('pages.traffic.periodDistribution', { defaultValue: 'Period Revenue Distribution' })}
                                    icon={Sparkles}
                                    color="text-amber-500"
                                    bgColor="bg-amber-500"
                                    externalHoverSource={hoveredSource}
                                    onHoverChange={setHoveredSource}
                                />
                            </div>
                        </div>
                        <ConversionAttributionTable
                            metricMappings={metricMappings}
                            selectedMetricName={selectedMetricName}
                            setSelectedMetricName={setSelectedMetricName}
                            activeMetric={activeMetric}
                            isMetricDrawerOpen={isMetricDrawerOpen}
                            setIsMetricDrawerOpen={setIsMetricDrawerOpen}
                            pendingMetricName={pendingMetricName}
                            setPendingMetricName={setPendingMetricName}
                            mappingCardRef={mappingCardRef}
                            setSelectedSourceLink={setSelectedSourceLink}
                            setInfoModal={setInfoModal}
                            formatCurrency={formatCurrency}
                            currencySymbol={currencySymbol}
                            t={t}
                        />
                    </div>
                </div>

                <BoardBottomDrawer
                    isOpen={!!(selectedSourceLink || isAiMobileExpanded)}
                    onClose={() => {
                        setSelectedSourceLink(null);
                        setIsAiMobileExpanded(false);
                    }}
                    onOpen={() => {
                        setIsAiMobileExpanded(true);
                    }}
                    height="100%"
                    initialHeight={selectedSourceLink ? 0 : "84px"}
                    showTints={!selectedSourceLink}
                    className={!selectedSourceLink ? 'lg:hidden' : ''}
                >
                    {!persistentSourceLink ? (
                         <div className="cursor-pointer" onClick={() => !isAiMobileExpanded && setIsAiMobileExpanded(true)}>
                            <div className="text-[10px] font-bold text-accent-primary uppercase tracking-widest mb-3 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-accent-primary animate-pulse" />
                                    {t('pages.traffic.aiDiagnosis')}
                                </div>
                            </div>
                            <h4 className={`font-bold text-text-primary mb-2 transition-all ${isAiMobileExpanded ? 'text-lg' : 'text-sm'}`}>
                                {t('pages.traffic.trafficVerdict')}
                            </h4>

                            {isAiMobileExpanded && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.2 }}
                                    className="pt-2"
                                >
                                    <AIVerdictModule 
                                        pageId="traffic" 
                                        boardId={boardId || ''} 
                                    />
                                </motion.div>
                            )}
                        </div>
                    ) : (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2 }}
                            className="flex flex-col h-full overflow-hidden"
                        >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-6 mb-8 px-1">
                                <div className="flex items-center gap-3">
                                    <span className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest shrink-0 ${persistentSourceLink.isPaid ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'}`}>
                                        {persistentSourceLink.isPaid ? t('pages.traffic.table.paid') : t('pages.traffic.table.organic')}
                                    </span>
                                    <h3 className="text-lg font-black text-neutral-900 dark:text-neutral-100 truncate">{persistentSourceLink.source}</h3>
                                </div>
                                <div className="flex flex-col sm:text-right border-l-4 sm:border-l-0 border-accent-primary/20 pl-3 sm:pl-0 py-1 sm:py-0">
                                    <div className="text-xl font-black text-accent-primary truncate">
                                        {formatCurrency(persistentSourceLink.revenue, currencySymbol)}
                                    </div>
                                    <div className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                                        {t('pages.traffic.totalSourceRevenue')}
                                    </div>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto pr-2 scrollbar-hide">
                                <RevenueSourceBreakdownUI
                                    segments={persistentSourceLink.matches.map((m: any) => ({
                                        name: m.name,
                                        parentName: persistentSourceLink.source,
                                        isPaid: persistentSourceLink.isPaid,
                                        revenue: m.revenue,
                                        attribution: persistentSourceLink.revenue > 0 ? (m.revenue / persistentSourceLink.revenue) * 100 : 0,
                                        channelName: m.channelName,
                                        isDisconnected: m.isDisconnected,
                                        isMatched: m.isMatched,
                                        isSelected: m.isSelected
                                    }))}
                                    formatCurrency={formatCurrency}
                                    currencySymbol={currencySymbol}
                                    t={t}
                                />
                            </div>
                        </motion.div>
                    )}
                </BoardBottomDrawer>
            </div>

            <div className="px-4 py-2 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 flex items-center justify-between shrink-0 z-20">
                <div className="flex-1 overflow-x-auto scrollbar-hide flex items-center gap-2 mr-6">
                    {periods.map((p, idx) => (
                        <button
                            key={idx}
                            onClick={() => setSelectedPeriodIndex(idx)}
                            className={`whitespace-nowrap px-4 py-1.5 text-xs font-medium rounded-full transition-all flex-shrink-0 border ${selectedPeriodIndex === idx
                                ? 'bg-neutral-800 text-white border-neutral-800 dark:bg-white dark:text-neutral-900 dark:border-white shadow-sm'
                                : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700 dark:hover:border-neutral-600'
                                }`}
                        >
                            {p.title}
                        </button>
                    ))}
                </div>

                <div className="flex bg-neutral-100 dark:bg-neutral-800 p-0.5 rounded-lg border border-neutral-200 dark:border-neutral-700 shrink-0">
                    <button
                        className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md transition-all ${viewMode === 'all' ? 'bg-white dark:bg-neutral-700 shadow-sm text-neutral-800 dark:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'}`}
                        onClick={() => { setViewMode('all'); setSelectedPeriodIndex(0); }}
                    >
                        {t('charts.components.all', 'All')}
                    </button>
                    <button
                        className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md transition-all ${viewMode === 'weeks' ? 'bg-white dark:bg-neutral-700 shadow-sm text-neutral-800 dark:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'}`}
                        onClick={() => { setViewMode('weeks'); setSelectedPeriodIndex(0); }}
                    >
                        {t('charts.components.weeks')}
                    </button>
                    <button
                        className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md transition-all ${viewMode === 'months' ? 'bg-white dark:bg-neutral-700 shadow-sm text-neutral-800 dark:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'}`}
                        onClick={() => { setViewMode('months'); setSelectedPeriodIndex(0); }}
                    >
                        {t('charts.components.months')}
                    </button>
                </div>
            </div>

            <InfoModal
                isOpen={infoModal.isOpen}
                onClose={() => setInfoModal(prev => ({ ...prev, isOpen: false }))}
                title={infoModal.title}
                description={infoModal.description}
            />
        </div>
    );
};
